import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  MessageSquare,
  ChartNoAxesGantt,
  Files,
  CheckCheck,
  FilePenLine,
} from "lucide-react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useGetPursuit } from "@/features/pursuit/useGetPursuit";
import { useEffect, useState } from "react";
import DocumentSkeleton from "@/ui/DocumentSkeleton";
import { PermissionClaim } from "supertokens-auth-react/recipe/userroles";
import Session from "supertokens-auth-react/recipe/session";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import ManageBillingButton from "../settings/ManageBillingButton";

const tabs = [
  {
    id: "overview",
    name: "Overview",
    icon: ChartNoAxesGantt,
  },
  {
    id: "tasks",
    name: "Tasks",
    icon: CheckCheck,
  },
  {
    id: "chat",
    name: "Chat",
    icon: MessageSquare,
  },
  {
    id: "documents",
    name: "Documents",
    icon: Files,
  },
  {
    id: "proposal",
    name: "Proposal",
    icon: FilePenLine,
  },
];

export function PursuitLayout() {
  const { pursuit_id } = useParams();
  const { data: pursuit, isLoading } = useGetPursuit(pursuit_id);
  const location = useLocation();
  const navigate = useNavigate();
  const claimValue = Session.useClaimValue(PermissionClaim);
  const permissions = claimValue.value;
  const getTabId = () =>
    tabs.find((tab) => location.pathname.split("/").includes(tab.id))?.id;

  // Extract tab name from URL
  const [currentTab, setCurrentTab] = useState(getTabId());
  useEffect(() => {
    setCurrentTab(getTabId());
  }, [location]);

  if (isLoading) {
    return <DocumentSkeleton sections={1} />;
  }
  const disableProposalGen =
    isLoading ||
    !(Array.isArray(permissions) && permissions.includes("proposal")) ||
    pursuit?.search_type === "sbir";
  return (
    <div className="h-full">
      <Tabs
        className="space-y-4 h-full"
        value={currentTab}
        onValueChange={(tab) => {
          setCurrentTab(tab);
          navigate(`./${tab}`);
        }}
      >
        <div className="sticky z-10 top-0 px-4 w-full border-b bg-background">
          <TabsList className="bg-transparent h-12 p-0 border-b-0">
            {tabs.map(({ id, name, icon: Icon }) =>
              id === "proposal" ? (
                <TooltipProvider delayDuration={300} key={id}>
                  <Tooltip>
                    <TooltipTrigger>
                      <TabsTrigger
                        key={id}
                        value={id}
                        disabled={disableProposalGen}
                        onClick={() =>
                          navigate(`/journey-hub/${pursuit_id}/${id}`)
                        }
                        className="h-12 px-4 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-primary relative rounded-none"
                      >
                        {Icon && <Icon className="h-4 w-4 mr-2" />}
                        {name}
                      </TabsTrigger>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        {Array.isArray(permissions) &&
                        permissions.includes("proposal") ? (
                          "Generate a tailored proposal for this opportunity using AI"
                        ) : (
                          <ManageBillingButton
                            variant="link"
                            text="Upgrade to generate proposals"
                            className="text-sm p-0 h-full"
                          />
                        )}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <TabsTrigger
                  key={id}
                  value={id}
                  onClick={() => navigate(`/journey-hub/${pursuit_id}/${id}`)}
                  className="h-12 px-4 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-primary relative rounded-none"
                >
                  {Icon && <Icon className="h-4 w-4 mr-2" />}
                  {name}
                </TabsTrigger>
              )
            )}
          </TabsList>
        </div>

        {tabs.some((tab) => tab.id === currentTab) ? (
          tabs.map(({ id }) => (
            <TabsContent
              key={id}
              value={id}
              className={`flex-1 mt-0 focus-visible::ring-0  h-full ${
                id === "tasks" ? "p-0" : "p-4 bg-[var(--gray-50)]"
              }`}
              style={{
                marginTop: 0,
              }}
            >
              {id === "tasks" ? (
                <Outlet context={{ pursuit, setCurrentTab }} />
              ) : (
                <div className="container">
                  <Outlet context={{ pursuit, setCurrentTab }} />
                </div>
              )}
            </TabsContent>
          ))
        ) : (
          <div className="flex-1 p-4 container">
            <Outlet context={{ pursuit, setCurrentTab }} />
          </div>
        )}
      </Tabs>
    </div>
  );
}
